/**
 * Resume refine agent step graph.
 *
 * Mirrors src/lib/outreach/agent/step-graph.ts: typed step keys plus an explicit
 * dependency map. Declaring the plan up front is what lets the UI render the
 * whole checklist greyed out before the run starts and light rows up as they
 * land, instead of an append-only log.
 *
 * Strictly linear, so no scheduler is needed. The dependency map is kept anyway
 * because it documents the data flow — and the flow is the argument: nothing may
 * be written before the evidence for it has been mapped.
 *
 * Lives apart from run.ts on purpose. Client components import the copy and the
 * step order, and run.ts transitively pulls in @ai-sdk/openai and the API key;
 * importing them from there would drag the server-side agent into the browser
 * bundle.
 */

import type { AgentGenerationStep, AgentStepCopy } from '@/lib/agent-kit/types';
import type { ResumeRefineStepKey } from './types';

export const resumeRefineStepOrder: ResumeRefineStepKey[] = [
  'EXTRACT_TARGET',
  'MAP_EVIDENCE',
  'REWRITE',
  'CRITIQUE',
  'POLISH',
];

export const resumeRefineStepDependencies: Record<
  ResumeRefineStepKey,
  ResumeRefineStepKey[]
> = {
  EXTRACT_TARGET: [],
  MAP_EVIDENCE: ['EXTRACT_TARGET'],
  REWRITE: ['MAP_EVIDENCE'],
  CRITIQUE: ['REWRITE'],
  POLISH: ['CRITIQUE'],
};

/**
 * Present-progressive, because these are read while the step is running.
 *
 * Worth saying plainly what MAP_EVIDENCE does: users are rightly suspicious of
 * an AI rewriting their work history, and "checking what you can actually back
 * up" is the sentence that earns the trust the rest of the flow spends.
 */
export const resumeRefineStepCopy: AgentStepCopy<ResumeRefineStepKey> = {
  labels: {
    EXTRACT_TARGET: 'Reading what the job asks for',
    MAP_EVIDENCE: 'Checking what you can actually back up',
    REWRITE: 'Retargeting your resume',
    CRITIQUE: 'Checking nothing was invented',
    POLISH: 'Fixing what the check found',
  },
  descriptions: {
    EXTRACT_TARGET:
      'Separates the hard requirements from the nice-to-haves, and picks out the terms an applicant tracking system will screen on.',
    MAP_EVIDENCE:
      'Matches your real experience against those requirements and names the gaps — the things this resume genuinely cannot claim.',
    REWRITE:
      'Rewrites the summary and your bullets around the evidence that exists, leading with the strong matches and quietly de-emphasising what is irrelevant here.',
    CRITIQUE:
      'A second, independent model checks whether the rewrite invented or overstated anything, and code checks every technology named against your original resume.',
    POLISH: 'Applies the fixes. Skipped when the rewrite passes.',
  },
};

/** All steps PENDING — the plan, publishable before any work begins. */
export function initialResumeRefineSteps(): AgentGenerationStep<ResumeRefineStepKey>[] {
  return resumeRefineStepOrder.map((step_key) => ({ step_key, status: 'PENDING' }));
}

/** Bump when the prompts or the graph change, so stale traces are identifiable. */
export const RESUME_REFINE_AGENT_VERSION = 'v2';
